// React hook for the Admin section's permission gate. Reads the current
// staffer's permission set (stored at login, resynced from /v1/admin/me) and
// re-renders when it changes (login/logout/resync all emit onAdminAuthChange).
//
// Usage:
//   const canAdjust = useAdminPermission('wallet.adjust');
//   {canAdjust ? <AdjustForm/> : <ReadOnlyNote/>}
//
// The gate is a UX affordance, NOT the security boundary — admin-svc enforces
// the same matrix server-side (rbac.Allowed) and 403s regardless. Hiding the
// control just avoids offering an action the server would reject.

import { useEffect, useState } from 'react';
import {
  getAdminPermissions,
  hasAdminPermission,
  onAdminAuthChange,
  type AdminPermission,
} from './adminAuth';

/** The current staffer's whole permission set, re-read on any auth change.
 *  Used where the required permission is computed at runtime (the route guard
 *  and the landing-route resolver) rather than known at the call site. */
export function useAdminPermissionList(): AdminPermission[] {
  const [perms, setPerms] = useState<AdminPermission[]>(() => getAdminPermissions());
  useEffect(() => {
    const unsub = onAdminAuthChange(() => setPerms(getAdminPermissions()));
    setPerms(getAdminPermissions());
    return unsub;
  }, []);
  return perms;
}

/** True iff the current staffer's role grants `perm`. Re-renders on auth/perm
 *  change. */
export function useAdminPermission(perm: AdminPermission): boolean {
  const [allowed, setAllowed] = useState<boolean>(() => hasAdminPermission(perm));
  useEffect(() => {
    // Re-read on any auth-state change (login, logout, /me resync).
    const unsub = onAdminAuthChange(() => setAllowed(hasAdminPermission(perm)));
    // Sync once on mount in case the set changed before this effect ran.
    setAllowed(hasAdminPermission(perm));
    return unsub;
  }, [perm]);
  return allowed;
}
